import React, { useState, createContext, useContext, useMemo, Suspense } from 'react';
import { Button, Space, message, Popconfirm } from 'antd';
import { PurchaseInfo, PurchaseStatus } from '../../../types/process/purchase';
import purchaseApi from '../../../services/process/purchase';
import dayjs from 'dayjs';

// Lazy import for modals to optimize load
const UploadPurchaseInfoModal = React.lazy(() => import('../management/modals/UploadPurchaseInfoModal'));
const DeclareModal = React.lazy(() => import('../management/modals/DeclareModal'));
const FillImeiModal = React.lazy(() => import('../management/modals/FillImeiModal'));
const BatchDeclareModal = React.lazy(() => import('../management/modals/BatchDeclareModal'));
const ViewPurchaseModal = React.lazy(() => import('../management/modals/ViewPurchaseModal'));
const ViewOrderDetailModal = React.lazy(() => import('../financial/modals/ViewOrderDetailModal'));


const GenerateSignatureModal = React.lazy(() => import('../management/modals/GenerateSignatureModal'));

export type PurchaseTriggerTarget =
  | 'declare'
  | 'fillImei'
  | 'uploadPurchaseInfo'
  | 'generateSignature'
  | 'viewPurchase' // 查看采购详情
  | 'viewOrderDetail' // 查看订单详情
  | 'return'; // 退回


export type OpenUploadPurchaseInfoModalArgs = {
  purchase: PurchaseInfo;
};

export type OpenDeclareModalArgs = {
  purchase: PurchaseInfo;
  isReDeclare?: boolean;
};

export type OpenFillImeiModalArgs = {
  purchase: PurchaseInfo;
};

export type OpenViewPurchaseModalArgs = {
  purchase: PurchaseInfo;
};

export type OpenViewOrderDetailModalArgs = {
  purchase: PurchaseInfo;
};

export type OpenGenerateSignatureModalArgs = {
  purchase: PurchaseInfo;
};

// --- Actions Context ---
export interface PurchaseActionContextValue {
  onReload?: () => void;
  openUploadPurchaseInfoModal?: (args: OpenUploadPurchaseInfoModalArgs) => void;
  openDeclareModal?: (args: OpenDeclareModalArgs) => void;
  openFillImeiModal?: (args: OpenFillImeiModalArgs) => void;
  openBatchDeclareModal?: (args: { selectedRows: PurchaseInfo[] }) => void;
  openViewPurchaseModal?: (args: OpenViewPurchaseModalArgs) => void;
  openViewOrderDetailModal?: (args: OpenViewOrderDetailModalArgs) => void;
  openGenerateSignatureModal?: (args: OpenGenerateSignatureModalArgs) => void;
}
const PurchaseActionContext = createContext<PurchaseActionContextValue>({});
export const usePurchaseActionContext = () => useContext(PurchaseActionContext);

export const PurchaseTriggerActionProvider: React.FC<{
  onReload?: () => void;
  children: React.ReactNode;
}> = ({ onReload, children }) => {
  const [uploadInfoModalVisible, setUploadInfoModalVisible] = useState(false);
  const [uploadInfoArgs, setUploadInfoArgs] = useState<OpenUploadPurchaseInfoModalArgs | null>(null);

  const [declareModalVisible, setDeclareModalVisible] = useState(false);
  const [declareArgs, setDeclareArgs] = useState<OpenDeclareModalArgs | null>(null);

  const [fillImeiModalVisible, setFillImeiModalVisible] = useState(false);
  const [fillImeiArgs, setFillImeiArgs] = useState<OpenFillImeiModalArgs | null>(null);

  const [batchDeclareModalVisible, setBatchDeclareModalVisible] = useState(false);
  const [batchDeclareArgs, setBatchDeclareArgs] = useState<{ selectedRows: PurchaseInfo[] } | null>(null);

  const [viewPurchaseModalVisible, setViewPurchaseModalVisible] = useState(false);
  const [viewPurchaseArgs, setViewPurchaseArgs] = useState<OpenViewPurchaseModalArgs | null>(null);

  const [viewOrderDetailModalVisible, setViewOrderDetailModalVisible] = useState(false);
  const [viewOrderDetailArgs, setViewOrderDetailArgs] = useState<OpenViewOrderDetailModalArgs | null>(null);


  const [generateSignatureModalVisible, setGenerateSignatureModalVisible] = useState(false);
  const [generateSignatureArgs, setGenerateSignatureArgs] = useState<OpenGenerateSignatureModalArgs | null>(null);

  const value = useMemo<PurchaseActionContextValue>(
    () => ({
      onReload,
      openUploadPurchaseInfoModal: (args) => {
        setUploadInfoArgs(args);
        setUploadInfoModalVisible(true);
      },
      openDeclareModal: (args) => {
        setDeclareArgs(args);
        setDeclareModalVisible(true);
      },
      openFillImeiModal: (args) => {
        setFillImeiArgs(args);
        setFillImeiModalVisible(true);
      },
      openBatchDeclareModal: (args) => {
        setBatchDeclareArgs(args);
        setBatchDeclareModalVisible(true);
      },
      openViewPurchaseModal: (args) => {
        setViewPurchaseArgs(args);
        setViewPurchaseModalVisible(true);
      },
      openViewOrderDetailModal: (args) => {
        setViewOrderDetailArgs(args);
        setViewOrderDetailModalVisible(true);
      },
      openGenerateSignatureModal: (args) => {
        setGenerateSignatureArgs(args);
        setGenerateSignatureModalVisible(true);
      },
    }),
    [onReload]
  );


  return (
    <PurchaseActionContext.Provider value={value}>
      {children}

      <Suspense fallback={null}>
        {uploadInfoArgs && uploadInfoModalVisible && (
          <UploadPurchaseInfoModal
            open={uploadInfoModalVisible}
            purchase={uploadInfoArgs.purchase}
            onCancel={() => {
              setUploadInfoModalVisible(false);
              setUploadInfoArgs(null);
            }}
            onSuccess={() => {
              setUploadInfoModalVisible(false);
              setUploadInfoArgs(null);
              onReload?.();
            }}
          />
        )}

        {declareArgs && declareModalVisible && (
          <DeclareModal
            open={declareModalVisible}
            purchase={declareArgs.purchase}
            isReDeclare={declareArgs.isReDeclare}
            onCancel={() => {
              setDeclareModalVisible(false);
              setDeclareArgs(null);
            }}
            onSuccess={() => {
              setDeclareModalVisible(false);
              setDeclareArgs(null);
              onReload?.();
            }}
          />
        )}

        {fillImeiArgs && fillImeiModalVisible && (
          <FillImeiModal
            open={fillImeiModalVisible}
            purchase={fillImeiArgs.purchase}
            onCancel={() => {
              setFillImeiModalVisible(false);
              setFillImeiArgs(null);
            }}
            onSuccess={() => {
              setFillImeiModalVisible(false);
              setFillImeiArgs(null);
              onReload?.();
            }}
          />
        )}

        {batchDeclareArgs && batchDeclareModalVisible && (
          <BatchDeclareModal
            open={batchDeclareModalVisible}
            selectedRows={batchDeclareArgs.selectedRows}
            onCancel={() => {
              setBatchDeclareModalVisible(false);
              setBatchDeclareArgs(null);
            }}
            onSuccess={() => {
              setBatchDeclareModalVisible(false);
              setBatchDeclareArgs(null);
              onReload?.();
            }}
          />
        )}

        {viewPurchaseArgs && viewPurchaseModalVisible && (
          <ViewPurchaseModal
            open={viewPurchaseModalVisible}
            purchase={viewPurchaseArgs.purchase}
            onCancel={() => {
              setViewPurchaseModalVisible(false);
              setViewPurchaseArgs(null);
            }}
          />
        )}

        {viewOrderDetailArgs && viewOrderDetailModalVisible && (
          <ViewOrderDetailModal
            open={viewOrderDetailModalVisible}
            purchase={viewOrderDetailArgs.purchase}
            onCancel={() => {
              setViewOrderDetailModalVisible(false);
              setViewOrderDetailArgs(null);
            }}
          />
        )}

        {generateSignatureArgs && generateSignatureModalVisible && (
          <GenerateSignatureModal
            open={generateSignatureModalVisible}
            purchase={generateSignatureArgs.purchase}
            onCancel={() => {
              setGenerateSignatureModalVisible(false);
              setGenerateSignatureArgs(null);
            }}
            onSuccess={() => {
              setGenerateSignatureModalVisible(false);
              setGenerateSignatureArgs(null);
              onReload?.();
            }}
          />
        )}
      </Suspense>

    </PurchaseActionContext.Provider>
  );
};


interface PurchaseTriggerActionMapProps {
  targets: PurchaseTriggerTarget[];
  purchase: PurchaseInfo;
  onReload?: () => void;
  ctx?: any;
}

// --- Action Map Component ---
const PurchaseTriggerActionMap: React.FC<PurchaseTriggerActionMapProps> = ({ targets, purchase, onReload }) => {
  const context = useContext(PurchaseActionContext);
  const effectiveReload = onReload || context.onReload;

  const handleAction = async (action: string, values?: any) => {
    try {
      await purchaseApi.changeStatus(purchase.purchase.purchaseNo, action, values);
      message.success('操作成功');
      if (effectiveReload) effectiveReload();
    } catch (err: any) {
      message.error(err.message || '操作失败');
    }
  };

  const triggerMap: Record<PurchaseTriggerTarget, () => React.ReactNode | null> = {
    declare: () => {
      const hasVoucher = !!purchase.purchase.purchaseVoucherUrl;
      const btnText = hasVoucher ? '修改信息' : '完善信息';
      return (
        <Button size="small" type="primary" onClick={() => context.openDeclareModal?.({ purchase })}>
          {btnText}
        </Button>
      );
    },
    fillImei: () => (
      <Button size="small" type="primary" onClick={() => context.openFillImeiModal?.({ purchase })}>
        补填串码
      </Button>
    ),
    uploadPurchaseInfo: () => {
      if (purchase.purchase.status !== PurchaseStatus.QuotePending) return null;
      return (
        <Button size="small" type="primary" onClick={() => context.openUploadPurchaseInfoModal?.({ purchase })}>
          上传采购信息
        </Button>
      );
    },
    generateSignature: () => {
      // 满足以下条件展示生成微签按钮：
      // 1. 不存在微签生成时间 (signatureTime)
      // 2. 或者存在时间，但是不存在微签链接 (signatureUrl)，且时间距离现在超过 10 分钟
      const hasSignatureTime = !!purchase.purchase.signatureTime;
      const hasSignatureUrl = !!purchase.purchase.signatureUrl;
      const isOver10Mins = hasSignatureTime && dayjs().diff(dayjs(purchase.purchase.signatureTime), 'minute') > 10;

      if (hasSignatureTime && !(!hasSignatureUrl && isOver10Mins)) {
        return null;
      }

      return (
        <Button size="small" type="primary" onClick={() => context.openGenerateSignatureModal?.({ purchase })}>
          生成微签
        </Button>
      );
    },
    viewPurchase: () => (
      <Button size="small" type="primary" onClick={() => context.openViewPurchaseModal?.({ purchase })}>
        查看
      </Button>
    ),
    viewOrderDetail: () => (
      <Button size="small" type="primary" onClick={() => context.openViewOrderDetailModal?.({ purchase })}>
        查看详情
      </Button>
    ),
    return: () => (
      <Popconfirm title="确认退回吗？" onConfirm={() => handleAction('return')}>
        <Button size="small" danger>退回</Button>
      </Popconfirm>
    ),
  };


  const nodes = targets
    .map((t) => ({ target: t, node: triggerMap[t]?.() }))
    .filter((x): x is { target: PurchaseTriggerTarget; node: React.ReactNode } => x.node !== null && x.node !== undefined);

  if (nodes.length === 0) return null;

  return (
    <Space size={[8, 8]} wrap>
      {nodes.map((n, i) => (
        <React.Fragment key={`${n.target}_${i}`}>{n.node}</React.Fragment>
      ))}
    </Space>
  );
};

export default PurchaseTriggerActionMap;
